/** Rotas da aplicação. As telas carregam sob demanda (o celular baixa só o que abre). */
import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router';
import { DialogProvider, Loading, useDialog } from '@dotapps/ui';
import { loadTenantExtension, type TenantExtension } from '../tenants/registry';
import { TENANT_SLUG } from '../lib/tenant';
import { LoginPage } from '../features/auth/LoginPage';
import { AppShell } from './AppShell';
import { useAuth } from './auth';

const page = <T extends Record<string, React.ComponentType>>(load: () => Promise<T>, name: keyof T) =>
  lazy(async () => ({ default: (await load())[name] }));

const HomePage = page(() => import('../features/home/HomePage'), 'HomePage');
const ClientsPage = page(() => import('../features/clients/ClientsPage'), 'ClientsPage');
const PipelinePage = page(() => import('../features/pipeline/PipelinePage'), 'PipelinePage');
const AgendaPage = page(() => import('../features/agenda/AgendaPage'), 'AgendaPage');
const BudgetsPage = page(() => import('../features/budgets/BudgetsPage'), 'BudgetsPage');
const BudgetEditorPage = page(() => import('../features/budgets/BudgetEditorPage'), 'BudgetEditorPage');
const ProposalsPage = page(() => import('../features/proposals/ProposalsPage'), 'ProposalsPage');
const PublicProposalPage = page(() => import('../features/public/PublicProposalPage'), 'PublicProposalPage');
const ContractsPage = page(() => import('../features/contracts/ContractsPage'), 'ContractsPage');
const ProjectsPage = page(() => import('../features/projects/ProjectsPage'), 'ProjectsPage');
const ReportsPage = page(() => import('../features/reports/ReportsPage'), 'ReportsPage');
const LibraryPage = page(() => import('../features/library/LibraryPage'), 'LibraryPage');
const BriefingPage = page(() => import('../features/briefing/BriefingPage'), 'BriefingPage');
const TeamPage = page(() => import('../features/team/TeamPage'), 'TeamPage');

const s = (el: ReactNode) => <Suspense fallback={<Loading />}>{el}</Suspense>;

/** Área logada: sem sessão, mostra a tela de entrada. */
function Private({ extension }: { extension: TenantExtension }) {
  const { user, ready } = useAuth();
  useEffect(() => {
    document.body.classList.toggle('logged-out', !user);
  }, [user]);
  if (!ready) return <Loading />;
  if (!user) {
    return (
      <>
        <header className="topbar">
          <div className="bar" />
        </header>
        <main id="view" className="view-in">
          <LoginPage />
        </main>
      </>
    );
  }
  return <AppShell extension={extension} />;
}

/** Fecha a janela aberta quando a pessoa muda de tela. */
function CloseDialogsOnNavigate() {
  const { pathname } = useLocation();
  const { close } = useDialog();
  // ao trocar de tela, fecha a janela aberta (a limpeza do efeito roda a cada mudança de endereço)
  useEffect(() => () => close(), [pathname, close]);
  return null;
}

/** Janelas ficam dentro do roteador para poderem navegar entre telas. */
function Root() {
  return (
    <DialogProvider>
      <CloseDialogsOnNavigate />
      <Outlet />
    </DialogProvider>
  );
}

function buildRouter(extension: TenantExtension) {
  return createBrowserRouter([
    {
      element: <Root />,
      children: [
        { path: '/p/:token', element: s(<PublicProposalPage />) },
        {
          path: '/',
          element: <Private extension={extension} />,
          children: [
            { index: true, element: s(<HomePage />) },
            { path: 'negociacao', element: s(<PipelinePage />) },
            { path: 'cadastros', element: s(<ClientsPage />) },
            { path: 'briefing', element: s(<BriefingPage />) },
            { path: 'agenda', element: s(<AgendaPage />) },
            { path: 'orcamentos', element: s(<BudgetsPage />) },
            { path: 'orcamentos/:id', element: s(<BudgetEditorPage />) },
            { path: 'propostas', element: s(<ProposalsPage />) },
            { path: 'propostas/:id', element: s(<ProposalsPage />) },
            { path: 'contratos', element: s(<ContractsPage />) },
            { path: 'contratos/:id', element: s(<ContractsPage />) },
            { path: 'obras', element: s(<ProjectsPage />) },
            { path: 'obras/:id', element: s(<ProjectsPage />) },
            { path: 'relatorios', element: s(<ReportsPage />) },
            { path: 'biblioteca', element: s(<LibraryPage />) },
            { path: 'equipe', element: s(<TeamPage />) },
            ...(extension.routes ?? []).map(r => ({ path: r.path.replace(/^\//, ''), element: s(r.element) })),
            { path: '*', element: <Navigate to="/" replace /> },
          ],
        },
      ],
    },
  ]);
}

export function App() {
  const [router, setRouter] = useState<ReturnType<typeof buildRouter> | null>(null);
  useEffect(() => {
    loadTenantExtension(TENANT_SLUG).then(ext => setRouter(buildRouter(ext)));
  }, []);
  if (!router) return <Loading />;
  return <RouterProvider router={router} />;
}
